// frontend/src/modules/notifications/pages/NotificationsPage.tsx
//
// Full-page notifications view for the tenant dashboard. Complements the
// header NotificationCenter dropdown: same data (GET /api/notifications),
// but with an All / Unread filter, a "mark all read" action, and room to
// show the full list rather than the compact dropdown.

import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, SlidersHorizontal } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { VISUAL } from '../../../design-system/visual';
import { formatDateTime } from '../../../design-system/format';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { ApiError } from '../../../core/api-client';
import { listNotifications, markRead, markAllRead } from '../api';
import type { Notification } from '../types';

/** Compact relative time (falls back to absolute for older items). */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDateTime(iso);
}

type Filter = 'all' | 'unread';

export function NotificationsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = React.useState<Filter>('all');
  const [items, setItems] = React.useState<Notification[] | null>(null);
  const [loadErr, setLoadErr] = React.useState<string | null>(null);

  const load = React.useCallback(() => {
    setItems(null);
    setLoadErr(null);
    listNotifications({ limit: 100, unread: filter === 'unread' })
      .then(({ notifications }) => setItems(notifications))
      .catch((e) => setLoadErr(e instanceof ApiError ? e.message : String((e as any)?.message ?? e)));
  }, [filter]);

  React.useEffect(() => { load(); }, [load]);

  const hasUnread = React.useMemo(() => (items ?? []).some((n) => !n.read_at), [items]);

  async function onItemClick(n: Notification) {
    if (!n.read_at) {
      // Optimistic mark-read, then persist.
      setItems((prev) => prev?.map((i) => (i.id === n.id ? { ...i, read_at: new Date().toISOString() } : i)) ?? prev);
      markRead(n.id).catch(() => { /* best-effort */ });
    }
    if (n.link) navigate(n.link);
  }

  async function onMarkAll() {
    setItems((prev) => prev?.map((i) => (i.read_at ? i : { ...i, read_at: new Date().toISOString() })) ?? prev);
    try { await markAllRead(); } catch { /* best-effort */ }
    // When viewing the Unread filter, reads should drop out of the list.
    if (filter === 'unread') load();
  }

  return (
    <div>
      <PageHeader
        eyebrow="Comms / Notifications"
        title="Notifications"
        description="Everything worth your attention across your workspace, in one place."
        actions={
          <div className="flex items-center gap-2">
            {hasUnread && (
              <Button
                variant="ghost"
                onClick={onMarkAll}
                className="rounded-full px-4 h-9 text-[13px] font-semibold gap-1.5"
              >
                <CheckCheck className="h-4 w-4" />
                Mark all read
              </Button>
            )}
            {/* The per-user preferences live on the Settings page; without a
                link from here they were effectively undiscoverable. */}
            <Link
              to="/dashboard/settings?section=notifications"
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white/80 dark:bg-white/[0.08] text-ink dark:text-white text-[12.5px] font-semibold shadow-pill ring-1 ring-ink/[0.08] dark:ring-white/10 hover:bg-white dark:hover:bg-white/[0.14] transition-colors"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Preferences
            </Link>
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-5">
        {/* Filter segmented control */}
        <div
          role="radiogroup"
          aria-label="Filter notifications"
          className="inline-flex items-center p-1 rounded-full bg-muted/50 ring-1 ring-border/40"
        >
          {(['all', 'unread'] as Filter[]).map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                role="radio"
                aria-checked={active}
                onClick={() => setFilter(f)}
                className={cn(
                  'inline-flex items-center rounded-full px-4 py-1.5 text-[12.5px] font-semibold capitalize transition-colors',
                  active
                    ? 'bg-ink-900 text-white shadow-pill dark:bg-coral'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {f}
              </button>
            );
          })}
        </div>

        {loadErr && <ErrorState description={loadErr} onRetry={load} />}
        {items === null && !loadErr && <LoadingState variant="inline" />}

        {items && items.length === 0 && (
          <div className={[VISUAL.bento, 'py-16 px-6 text-center'].join(' ')}>
            <Bell className="h-7 w-7 mx-auto text-muted-foreground/40" />
            <p className="mt-3 text-[14px] text-muted-foreground">
              {filter === 'unread' ? 'No unread notifications.' : "You're all caught up."}
            </p>
          </div>
        )}

        {items && items.length > 0 && (
          <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
            <ul className="divide-y divide-border/40">
              {items.map((n) => {
                const clickable = Boolean(n.link);
                const body = (
                  <div className="flex gap-3.5 w-full text-left">
                    <span
                      aria-hidden
                      className={cn('mt-1.5 size-2 rounded-full shrink-0', n.read_at ? 'bg-transparent' : 'bg-coral')}
                    />
                    <div className="min-w-0 flex-1">
                      <p className={cn(
                        'text-[14px]',
                        n.read_at ? 'font-medium text-foreground/80' : 'font-semibold text-foreground',
                      )}>
                        {n.title}
                      </p>
                      {n.body && (
                        <p className="text-[12.5px] text-muted-foreground mt-0.5 leading-relaxed">{n.body}</p>
                      )}
                      <p className="text-[11px] text-muted-foreground/70 mt-1.5">{relativeTime(n.created_at)}</p>
                    </div>
                  </div>
                );
                return (
                  <li key={n.id}>
                    {clickable ? (
                      <button
                        type="button"
                        onClick={() => onItemClick(n)}
                        className="w-full px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors"
                      >
                        {body}
                      </button>
                    ) : (
                      <div className="px-5 sm:px-6 py-4">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
